import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Copy, Trash2, Send, X, Info, Wind } from 'lucide-react';

const TaskBasedTimesheetSubmit = () => {
  const [projects, setProjects] = useState([]);
  const [projectTurbines, setProjectTurbines] = useState([]);
  const [loadingTurbines, setLoadingTurbines] = useState(false);
  const [forms, setForms] = useState([{
    projekt: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    end_time: '16:00',
    break_duration: 30,
    note: '',
    // Task-based fields
    project_turbine: '',
    task_category: '',
    task_subcategory: ''
  }]);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibleDays, setVisibleDays] = useState(7);
  const [showInfoBox, setShowInfoBox] = useState(true);

  useEffect(() => {
    fetchProjects();
    fetchVisibilitySettings();
  }, []);

  // Fetch turbines when project changes
  useEffect(() => {
    const projectId = forms[0]?.projekt;
    if (projectId) {
      fetchProjectTurbines(projectId);
    } else {
      setProjectTurbines([]);
    }
  }, [forms[0]?.projekt]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user-active-projects/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setProjects(response.data || []);
    } catch (error) { console.error('Error fetching projects:', error); }
  };

  const fetchProjectTurbines = async (projectId) => {
    try {
      setLoadingTurbines(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/project-turbines/?project=${projectId}&include_tasks=true`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setProjectTurbines(response.data || []);
    } catch (error) {
      console.error('Error fetching project turbines:', error);
      setProjectTurbines([]);
    } finally {
      setLoadingTurbines(false);
    }
  };

  const fetchVisibilitySettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/visibility-settings/me/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setVisibleDays(response.data.days_visible || 7);
    } catch (error) { setVisibleDays(7); }
  };

  const calculateWorkedHours = (startTime, endTime, breakDuration) => {
    if (!startTime || !endTime) return '0h 0min';
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    let totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    if (totalMinutes < 0) totalMinutes += 24 * 60;
    totalMinutes -= breakDuration;
    return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}min`;
  };

  const getSelectedTurbine = (turbineId) => {
    return projectTurbines.find(t => t.id === parseInt(turbineId));
  };

  const getTaskCategories = (turbineId) => {
    const turbine = getSelectedTurbine(turbineId);
    if (!turbine?.task_list_data?.categories) return [];
    return turbine.task_list_data.categories;
  };

  const getTaskSubcategories = (turbineId, categoryId) => {
    const categories = getTaskCategories(turbineId);
    const category = categories.find(c => c.id === parseInt(categoryId));
    return category?.subcategories || [];
  };

  const handleFormChange = (index, field, value) => {
    const newForms = [...forms];
    newForms[index] = { ...newForms[index], [field]: value };

    // Reset dependent fields
    if (field === 'projekt') {
      newForms[index].project_turbine = '';
      newForms[index].task_category = '';
      newForms[index].task_subcategory = '';
    }
    if (field === 'project_turbine') {
      newForms[index].task_category = '';
      newForms[index].task_subcategory = '';
    }
    if (field === 'task_category') {
      newForms[index].task_subcategory = '';
    }

    setForms(newForms);
  };

  const addForm = () => {
    const lastForm = forms[forms.length - 1];
    const [endHour, endMin] = lastForm.end_time.split(':').map(Number);
    let newEndHour = endHour + 4;
    if (newEndHour > 23) newEndHour = 23;
    const newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
    setForms([...forms, {
      projekt: lastForm.projekt,
      date: lastForm.date,
      start_time: lastForm.end_time,
      end_time: newEndTime,
      break_duration: 0,
      note: '',
      project_turbine: lastForm.project_turbine,
      task_category: '',
      task_subcategory: ''
    }]);
  };

  const removeForm = (index) => {
    if (forms.length === 1) { alert('You must have at least one form'); return; }
    setForms(forms.filter((_, i) => i !== index));
  };

  const copyForm = (index) => {
    const formToCopy = forms[index];
    setForms([...forms, { ...formToCopy, note: '' }]);
  };

  const validateForm = (form, index) => {
    const formErrors = [];
    if (!form.projekt) formErrors.push(`Entry #${index + 1}: Select a project`);
    if (!form.project_turbine) formErrors.push(`Entry #${index + 1}: Select a turbine`);
    if (!form.task_category) formErrors.push(`Entry #${index + 1}: Select a task category`);
    if (!form.task_subcategory) formErrors.push(`Entry #${index + 1}: Select a task`);
    if (!form.date) {
      formErrors.push(`Entry #${index + 1}: Select a date`);
    } else {
      const selectedDateStr = form.date;
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() - visibleDays);
      const minDateStr = minDate.toISOString().split('T')[0];
      if (selectedDateStr < minDateStr || selectedDateStr > todayStr) {
        formErrors.push(`Entry #${index + 1}: Date must be within the last ${visibleDays} days`);
      }
    }
    if (!form.start_time || !form.end_time) formErrors.push(`Entry #${index + 1}: Select start and end times`);
    if (form.break_duration < 0 || form.break_duration > 480) formErrors.push(`Entry #${index + 1}: Break must be between 0 and 480 minutes`);
    return formErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccess('');
    const allErrors = [];
    forms.forEach((form, index) => allErrors.push(...validateForm(form, index)));
    if (allErrors.length > 0) { setErrors(allErrors); return; }
    setLoading(true);

    const formData = new FormData();
    formData.append('form-TOTAL_FORMS', forms.length.toString());
    formData.append('form-INITIAL_FORMS', '0');
    formData.append('form-MAX_NUM_FORMS', '');

    forms.forEach((form, index) => {
      // Get category and subcategory names for the type field
      const categories = getTaskCategories(form.project_turbine);
      const category = categories.find(c => c.id === parseInt(form.task_category));
      const subcategories = category?.subcategories || [];
      const subcategory = subcategories.find(s => s.id === parseInt(form.task_subcategory));

      // Map task names to category/type fields (like mobile does)
      const categoryName = category?.name || 'Work';
      const typeName = subcategory?.name || category?.name || 'Work(Day)';

      formData.append(`form-${index}-projekt`, form.projekt);
      formData.append(`form-${index}-date`, form.date);
      formData.append(`form-${index}-start_time`, form.start_time);
      formData.append(`form-${index}-end_time`, form.end_time);
      formData.append(`form-${index}-break_duration`, form.break_duration.toString());
      formData.append(`form-${index}-category`, categoryName);
      formData.append(`form-${index}-type`, typeName);
      formData.append(`form-${index}-note`, form.note || '');
      // Task-based fields
      formData.append(`form-${index}-project_turbine`, form.project_turbine);
      formData.append(`form-${index}-task_category`, form.task_category);
      if (form.task_subcategory) {
        formData.append(`form-${index}-task_subcategory`, form.task_subcategory);
      }
    });

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/omega/submit/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setSuccess(`Successfully added ${forms.length} timesheet(s)!`);
      setForms([{
        projekt: '', date: new Date().toISOString().split('T')[0], start_time: '08:00', end_time: '16:00',
        break_duration: 30, note: '', project_turbine: '', task_category: '', task_subcategory: ''
      }]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(Array.isArray(error.response.data.errors) ? error.response.data.errors : [error.response.data.errors]);
      } else if (error.response?.data?.error) {
        setErrors([error.response.data.error]);
      } else {
        setErrors(['An error occurred while saving timesheets.']);
      }
    } finally { setLoading(false); }
  };

  const getDateRangeInfo = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - visibleDays);
    return { minDate: minDate.toISOString().split('T')[0], maxDate: today.toISOString().split('T')[0] };
  };

  const dateRange = getDateRangeInfo();

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900">Submit Timesheets</h1>
          <p className="text-xs text-slate-500">Add timesheet entries based on assigned tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 bg-brand-100 text-brand-600 rounded-xl text-xs font-bold">
            Task-Based
          </span>
          <span className="px-3 py-1.5 bg-brand-100 text-brand-600 rounded-xl text-xs font-bold">
            {forms.length} {forms.length === 1 ? 'Entry' : 'Entries'}
          </span>
        </div>
      </div>

      {/* Info Box */}
      {showInfoBox && (
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
            <Info className="h-4 w-4 text-brand-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-brand-800">
              <strong>Task-Based Mode:</strong> Select a turbine and task from your project's task list to log your work hours.
            </p>
          </div>
          <button onClick={() => setShowInfoBox(false)} className="p-1 rounded-lg hover:bg-brand-100 text-brand-400">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
            <svg className="h-4 w-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm font-medium text-brand-800">{success}</p>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-red-100 flex items-center justify-center">
              <X className="h-3 w-3 text-red-600" />
            </div>
            <p className="text-sm font-bold text-red-800">Please fix the following errors:</p>
          </div>
          <ul className="list-disc list-inside space-y-1 ml-8">
            {errors.map((error, index) => (
              <li key={index} className="text-sm text-red-700">{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {forms.map((form, index) => {
          const workedHours = calculateWorkedHours(form.start_time, form.end_time, form.break_duration);
          const taskCategories = getTaskCategories(form.project_turbine);
          const taskSubcategories = getTaskSubcategories(form.project_turbine, form.task_category);
          const selectedTurbine = getSelectedTurbine(form.project_turbine);

          return (
            <div key={index} className={`bg-white rounded-2xl border ${index === 0 ? 'border-brand-200' : 'border-steel-200'} shadow-sm overflow-hidden`}>
              {/* Card Header */}
              <div className="px-4 py-3 border-b border-steel-100 bg-steel-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${index === 0 ? 'bg-brand-100' : 'bg-steel-200'}`}>
                    <span className={`text-xs font-black ${index === 0 ? 'text-brand-600' : 'text-slate-600'}`}>#{index + 1}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-900">Entry #{index + 1}</span>
                  {index === 0 && <span className="px-2 py-0.5 bg-brand-100 text-brand-600 rounded-md text-[9px] font-bold uppercase">Main</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => copyForm(index)}
                    className="h-7 px-2 flex items-center gap-1 text-slate-600 hover:bg-steel-100 rounded-lg transition-colors"
                    title="Copy entry"
                  >
                    <Copy size={14} />
                    <span className="text-xs font-medium">Copy</span>
                  </button>
                  {forms.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeForm(index)}
                      className="h-7 px-2 flex items-center gap-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove entry"
                    >
                      <Trash2 size={14} />
                      <span className="text-xs font-medium">Remove</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-4">
                {/* Project & Date - only first entry */}
                {index === 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                        Project <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={form.projekt}
                        onChange={(e) => handleFormChange(index, 'projekt', e.target.value)}
                        className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                      >
                        <option value="">-- Select project --</option>
                        {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <span className="text-[9px] text-slate-400">Last {visibleDays} days</span>
                      </div>
                      <input
                        type="date"
                        value={form.date}
                        onChange={(e) => handleFormChange(index, 'date', e.target.value)}
                        min={dateRange.minDate}
                        max={dateRange.maxDate}
                        className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                  </div>
                )}

                {/* Turbine Selection - only first entry or if different project */}
                {index === 0 && form.projekt && (
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      <Wind size={12} className="inline mr-1" />
                      Turbine <span className="text-red-500">*</span>
                    </label>
                    {loadingTurbines ? (
                      <div className="h-10 px-3 bg-steel-50 border border-steel-200 rounded-xl flex items-center">
                        <span className="text-sm text-slate-400">Loading turbines...</span>
                      </div>
                    ) : (
                      <select
                        value={form.project_turbine}
                        onChange={(e) => handleFormChange(index, 'project_turbine', e.target.value)}
                        className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                      >
                        <option value="">-- Select turbine --</option>
                        {projectTurbines.map(turbine => (
                          <option key={turbine.id} value={turbine.id}>
                            T{turbine.turbine_number}{turbine.turbine_id ? ` (${turbine.turbine_id})` : ''}
                            {turbine.task_list_data?.name ? ` - ${turbine.task_list_data.name}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Time Row */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      Start <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={form.start_time}
                      onChange={(e) => handleFormChange(index, 'start_time', e.target.value)}
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      End <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={form.end_time}
                      onChange={(e) => handleFormChange(index, 'end_time', e.target.value)}
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      Break (min) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={form.break_duration}
                      onChange={(e) => handleFormChange(index, 'break_duration', parseInt(e.target.value) || 0)}
                      min="0"
                      max="480"
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Worked</label>
                    <div className="h-10 px-3 bg-brand-50 border border-brand-200 rounded-xl flex items-center">
                      <span className="text-sm font-black text-brand-600">{workedHours}</span>
                    </div>
                  </div>
                </div>

                {/* Task Category & Subcategory */}
                {(form.project_turbine || index > 0) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                        Task Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={form.task_category}
                        onChange={(e) => handleFormChange(index, 'task_category', e.target.value)}
                        disabled={!form.project_turbine && index === 0}
                        className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white disabled:bg-steel-100 disabled:text-slate-400"
                      >
                        <option value="">-- Select category --</option>
                        {taskCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                        Task <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={form.task_subcategory}
                        onChange={(e) => handleFormChange(index, 'task_subcategory', e.target.value)}
                        disabled={!form.task_category}
                        className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white disabled:bg-steel-100 disabled:text-slate-400"
                      >
                        <option value="">-- Select task --</option>
                        {taskSubcategories.map(sub => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Note */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Note</label>
                  <input
                    type="text"
                    value={form.note}
                    onChange={(e) => handleFormChange(index, 'note', e.target.value)}
                    maxLength={255}
                    placeholder="Optional note..."
                    className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </div>
          );
        })}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={addForm}
            disabled={loading}
            className="h-10 px-4 flex items-center gap-2 bg-white border border-steel-200 text-slate-700 rounded-xl hover:bg-steel-50 font-bold text-sm transition-colors disabled:opacity-50"
          >
            <Plus size={16} />
            Add Another Entry
          </button>

          <button
            type="submit"
            disabled={loading}
            className="h-10 px-6 flex items-center gap-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold text-sm uppercase tracking-wide transition-all shadow-lg shadow-brand-500/20 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send size={16} />
                <span>Send Timesheets ({forms.length})</span>
              </>
            )}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default TaskBasedTimesheetSubmit;
